import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { DataGrid } from 'v3/Components/DataGrid/DataGrid';
import { MultiSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/MultiSelectFilter';
import { TextFieldFilter } from 'v3/Components/DataGrid/ColumnFilters/TextFieldFilter';
import { DateRangePickerFilterFilter } from 'v3/Components/DataGrid/ColumnFilters/DateRangePickerFilter';
import Tag, { TagColor } from 'v3/Components/Shared/Tag';
import { TertiaryButton } from 'v3/Components/Shared/Buttons';
import { SecureComponentV3 } from 'Components/Security/SecureComponentV3';
import {
  buildEndpoint,
  defaultDataTransform,
  formatShortDateTime,
  parseUtcToLocal,
  useApiWorker,
  useDataFetcher,
  useHasPermissionsV3,
} from 'Utilities';
import { FeatureFlags, isFeatureEnabled } from 'Utilities/FeatureFlagProvider';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import CloudySvgUrl from 'Assets/Icons/cloudy.svg';
import {
  DocumentDrawer,
  ReviewerDocumentRow,
} from './ReviewerProfile.Documents.Drawer';
import './ReviewerProfile.Documents.scss';

type ReviewerDocumentTypeModel = {
  id: number;
  name: string;
  color: string;
  isInternalOnly: boolean;
};

const FORMAT_OPTIONS = ['PDF', 'DOC', 'DOCX', 'XLS', 'XLSX'];

const DocumentsEmptyState: React.FC = () => (
  <div className="rp-documents-empty">
    <img
      src={CloudySvgUrl}
      alt=""
      aria-hidden="true"
      className="rp-documents-empty__mascot"
    />
    <span className="rp-documents-empty__heading">
      No reviewer documents added
    </span>
    <span className="rp-documents-empty__subtext">
      START BY UPLOADING YOUR FIRST DOCUMENT
    </span>
  </div>
);

type ReviewerProfileDocumentsProps = {
  documents: ReviewerDocumentRow[];
  setDocuments: React.Dispatch<React.SetStateAction<ReviewerDocumentRow[]>>;
  isLoading: boolean;
};

const ReviewerProfileDocuments: React.FC<ReviewerProfileDocumentsProps> = ({
  documents,
  setDocuments,
  isLoading,
}) => {
  const { id: reviewerId } = useParams<{ id: string }>();
  const hasPermission = useHasPermissionsV3();
  const API = useApiWorker('3.0');

  const canEdit = hasPermission(PermissionCodesV3.Reviewer.Document.Edit);
  const canDownload = hasPermission(
    PermissionCodesV3.Reviewer.Document.Download
  );
  const canSearch = hasPermission(PermissionCodesV3.Reviewer.Document.Search);
  const canFilter = hasPermission(PermissionCodesV3.Reviewer.Document.Filter);
  // Internal staff have REVIEWER.DOCUMENT.VIEW; self-service reviewers have REVIEWER.SELF.VIEW only
  const canSeeInternalOnly = hasPermission(
    PermissionCodesV3.Reviewer.Document.View
  );

  const [drawerMode, setDrawerMode] = useState<'upload' | 'edit'>('upload');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<
    ReviewerDocumentRow | undefined
  >();
  const [columnFilterValues, setColumnFilterValues] = useState<
    Record<string, any>
  >({});

  const [documentTypes] = useDataFetcher<ReviewerDocumentTypeModel[]>(
    buildEndpoint(ENDPOINTS.reviewers.base, 'documentTypes'),
    [],
    defaultDataTransform,
    undefined,
    true,
    '3.0'
  );

  const handleUploadSuccess = (doc: ReviewerDocumentRow) => {
    setDocuments((prev) => [...prev, doc]);
    setDrawerOpen(false);
  };

  const handleEditSuccess = (updated: ReviewerDocumentRow) => {
    setDocuments((prev) =>
      prev.map((d) => (d.id === updated.id ? updated : d))
    );
    setDrawerOpen(false);
  };

  const handleDelete = (id: string) => {
    setDocuments((prev) => prev.filter((d) => d.id !== id));
    setDrawerOpen(false);
  };

  const openUpload = () => {
    setDrawerMode('upload');
    setSelectedDocument(undefined);
    setDrawerOpen(true);
  };

  const openEdit = (doc: ReviewerDocumentRow) => {
    setDrawerMode('edit');
    setSelectedDocument(doc);
    setDrawerOpen(true);
  };

  const handleDownload = async (doc: ReviewerDocumentRow) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.reviewers.base,
      reviewerId,
      'documents',
      doc.id,
      'download'
    );
    const response = await API.get(endpoint, { responseType: 'blob' });
    const blob = response.data as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.fileName || doc.title || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Derive unique uploaders and visible document types for filter dropdowns
  const uploaderOptions = useMemo(
    () =>
      Array.from(
        new Set(documents.map((d) => d.uploadedBy).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [documents]
  );

  const visibleTypeOptions = useMemo(
    () =>
      documentTypes
        .filter((t) => canSeeInternalOnly || !t.isInternalOnly)
        .map((t) => t.name),
    [documentTypes, canSeeInternalOnly]
  );

  // Apply column filter values to documents for client-side filtering
  const filteredDocuments = useMemo(() => {
    let result = documents;

    const { uploadDate, title, format, uploadedBy, documentType } =
      columnFilterValues;

    if (uploadDate) {
      const [startStr, endStr] = (uploadDate as string).split('|');
      const start = startStr ? new Date(startStr) : null;
      const end = endStr ? new Date(endStr) : null;
      result = result.filter((doc) => {
        const d = parseUtcToLocal(doc.uploadDate);
        if (!d) return false;
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
      });
    }

    if (title) {
      const q = (title as string).toLowerCase();
      result = result.filter((doc) => doc.title?.toLowerCase().includes(q));
    }

    if (Array.isArray(format) && format.length > 0) {
      result = result.filter((doc) =>
        format.includes(doc.format?.toUpperCase())
      );
    }

    if (Array.isArray(uploadedBy) && uploadedBy.length > 0) {
      result = result.filter((doc) =>
        uploadedBy.some((v) =>
          doc.uploadedBy?.toLowerCase().includes((v as string).toLowerCase())
        )
      );
    }

    if (Array.isArray(documentType) && documentType.length > 0) {
      result = result.filter(
        (doc) =>
          doc.documentType &&
          documentType.includes(
            (doc.documentType as ReviewerDocumentTypeModel).name
          )
      );
    }

    return result;
  }, [documents, columnFilterValues]);

  const columns: GridColDef[] = [
    {
      field: 'uploadDate',
      headerName: 'Date Uploaded',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <span>{formatShortDateTime(params.value)}</span>
      ),
      ...(canFilter && {
        renderHeaderFilter: (params: any) => (
          <DateRangePickerFilterFilter {...params} dateOnly />
        ),
      }),
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 200,
      ...(canFilter && {
        renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
      }),
    },
    {
      field: 'format',
      headerName: 'Format',
      width: 90,
      ...(canFilter && {
        renderHeaderFilter: (params: any) => (
          <MultiSelectFilter {...params} optionItems={FORMAT_OPTIONS} />
        ),
      }),
    },
    {
      field: 'uploadedBy',
      headerName: 'Uploaded By',
      width: 180,
      ...(canFilter && {
        renderHeaderFilter: (params: any) => (
          <MultiSelectFilter {...params} optionItems={uploaderOptions} />
        ),
      }),
    },
    {
      field: 'documentType',
      headerName: 'Type',
      width: 200,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<any, ReviewerDocumentTypeModel>
      ) => {
        const docType = params.value;
        if (!docType) return null;
        return <Tag label={docType.name} color={docType.color as TagColor} />;
      },
      ...(canFilter && {
        renderHeaderFilter: (params: any) => (
          <MultiSelectFilter {...params} optionItems={visibleTypeOptions} />
        ),
      }),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      align: 'center',
      display: 'flex',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <div className="rp-documents-actions">
          {canEdit ? (
            <button
              className="rp-documents-actions__btn"
              aria-label="Edit document"
              onClick={() => openEdit(params.row as ReviewerDocumentRow)}
            >
              <EditOutlinedIcon fontSize="small" />
            </button>
          ) : (
            <button
              className="rp-documents-actions__btn"
              disabled
              aria-label="Edit document"
            >
              <EditOutlinedIcon fontSize="small" />
            </button>
          )}
          {canDownload && (
            <button
              className="rp-documents-actions__btn"
              aria-label="Download document"
              onClick={() => handleDownload(params.row as ReviewerDocumentRow)}
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const uploadButton = (
    <SecureComponentV3
      permissions={[PermissionCodesV3.Reviewer.Document.Upload]}
    >
      <TertiaryButton
        title="Upload"
        iconPosition="start"
        onClick={openUpload}
      />
    </SecureComponentV3>
  );

  return (
    <div className="rp-documents">
      <DataGrid
        title="Documents"
        columns={columns}
        rows={filteredDocuments}
        loading={isLoading}
        headerActions={uploadButton}
        noRowsOverlay={DocumentsEmptyState}
        toolbarPermissions={{
          showColumns: false,
          showExport: false,
          showSearch: canSearch,
          showFilter: canFilter,
        }}
        initialState={{
          sorting: { sortModel: [{ field: 'uploadDate', sort: 'asc' }] },
        }}
        onFilterValuesChange={setColumnFilterValues}
      />

      <DocumentDrawer
        mode={drawerMode}
        reviewerId={reviewerId}
        document={selectedDocument}
        documentTypes={documentTypes}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSuccess={
          drawerMode === 'upload' ? handleUploadSuccess : handleEditSuccess
        }
        onDelete={handleDelete}
      />
    </div>
  );
};

export default ReviewerProfileDocuments;
